import { AdmProject } from "@custom-types/base-types";
import { createSlice } from "@reduxjs/toolkit";
import { FetchingStatus } from "@store/store-helper";
import { fetchProject, updateProject } from "@store/project/project-thunks";

/** State for project */
type ProjectState = {
  /** The selected project */
  project: AdmProject | null;

  /** Async operation statuses for project-slice */
  fetchingProject: {
    /** The status of fetching the selected project */
    selectedProject: FetchingStatus;

    /** The status of updating the selected project */
    updatingProject: FetchingStatus;
  };
};

/** Initial state for project slice */
const initialProjectState: ProjectState = {
  /** By default, no project is selected */
  project: null,

  /** Async operation statuses for project-slice */
  fetchingProject: {
    /** By default, the fetch status is uninitialized */
    selectedProject: "uninitialized",

    /** By default, the fetch status is uninitialized */
    updatingProject: "uninitialized",
  },
};

/** Slice to manage project state */
const projectSlice = createSlice({
  name: "project",
  initialState: initialProjectState,
  reducers: {
    /** Resets the project state to its initial values */
    resetProjectState: () => initialProjectState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProject.pending, (state) => {
        state.fetchingProject.selectedProject = "pending";
      })
      .addCase(fetchProject.fulfilled, (state, action) => {
        state.project = action.payload;
        state.fetchingProject.selectedProject = "succeeded";
      })
      .addCase(fetchProject.rejected, (state) => {
        state.fetchingProject.selectedProject = "rejected";
      })

      .addCase(updateProject.pending, (state) => {
        state.fetchingProject.updatingProject = "pending";
      })
      .addCase(updateProject.fulfilled, (state, action) => {
        state.project = state.project
          ? {
              ...state.project,
              name: action.payload.name,
              state: action.payload.archivingState,
              accessLevel: action.payload.accessLevel,
              area: { amount: action.payload.sqft, unit: "sqft" },
            }
          : state.project;
        state.fetchingProject.updatingProject = "succeeded";
      })
      .addCase(updateProject.rejected, (state) => {
        state.fetchingProject.updatingProject = "rejected";
      });
  },

  selectors: {
    /** Selector to get the entire project state */
    projectStateSelector: (state) => state,
  },
});

export const { resetProjectState } = projectSlice.actions;

export const { projectStateSelector } = projectSlice.selectors;

export const projectReducer = projectSlice.reducer;
